import { env } from "./env";
import { getLoginErrorMessage } from "./authError";

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  email: string;
}

const STORAGE_KEY = "line_queue_admin_session";

function readStored(): AuthSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthSession) : null;
  } catch {
    return null;
  }
}

function save(session: AuthSession | null) {
  if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  else window.localStorage.removeItem(STORAGE_KEY);
}

function authHeaders(accessToken?: string) {
  return {
    apikey: env.publishableKey,
    Authorization: `Bearer ${accessToken ?? env.publishableKey}`,
    "Content-Type": "application/json",
  };
}

export async function signIn(email: string, password: string): Promise<AuthSession> {
  const normalizedEmail = email.trim().toLowerCase();
  let response: Response;
  try {
    response = await fetch(`${env.supabaseUrl}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ email: normalizedEmail, password }),
    });
  } catch {
    throw new Error("เชื่อมต่อ Supabase Auth ไม่สำเร็จ กรุณาตรวจอินเทอร์เน็ตแล้วลองใหม่");
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(getLoginErrorMessage(response.status, body));
  if (
    typeof body.access_token !== "string" ||
    typeof body.refresh_token !== "string"
  ) {
    throw new Error("Supabase Auth ไม่ส่งข้อมูลเซสชันกลับมาอย่างครบถ้วน");
  }
  const session: AuthSession = {
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresAt: Date.now() + Number(body.expires_in ?? 3600) * 1000,
    email: body.user?.email ?? normalizedEmail,
  };
  save(session);
  return session;
}

export async function getSession(): Promise<AuthSession | null> {
  const current = readStored();
  if (!current) return null;
  if (current.expiresAt > Date.now() + 60_000) return current;

  const response = await fetch(`${env.supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ refresh_token: current.refreshToken }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    save(null);
    return null;
  }
  const refreshed: AuthSession = {
    accessToken: body.access_token,
    refreshToken: body.refresh_token ?? current.refreshToken,
    expiresAt: Date.now() + Number(body.expires_in ?? 3600) * 1000,
    email: body.user?.email ?? current.email,
  };
  save(refreshed);
  return refreshed;
}

export async function signOut() {
  const session = readStored();
  save(null);
  if (!session) return;
  await fetch(`${env.supabaseUrl}/auth/v1/logout`, {
    method: "POST",
    headers: authHeaders(session.accessToken),
  }).catch(() => undefined);
}
